<script lang="ts" setup>
  import BaseModal from "@/components/modals/BaseModal.vue";
  import { computed, ref } from "vue";
  import router from "@/router";
  import { useRewardsStore } from "@/stores/rewards";
  import { watchDebounced } from "@vueuse/core";
  import type { Reward } from "@/models/reward";

  const emit = defineEmits<{
    (e: 'close'): void,
  }>();

  const searchQuery = ref<string>('');
  const searchResults = ref<Reward[]>([]);
  const rewardsStore = useRewardsStore();

  // Compute whether to render the search results menu
  const renderMenu = computed(() => {
    return searchResults.value.length > 0;
  });

  // Watch for changes to the search query on a debounce
  watchDebounced(
    searchQuery,
    async (value) => {
      if (value.length > 0) {
        await rewardsStore.fetchSearchRewards(value);
        searchResults.value = rewardsStore.getSearchResults();
      } else {
        searchResults.value = [];
      }
    },
    {debounce: 250, maxWait: 500}
  );

  function selectResult(query: string) {
    emit('close');
    router.push({ name: 'rewards', query: { search: encodeURIComponent(query) }, force: true });
    rewardsStore.setSearchQuery(query);
  }

  function seeAllRewards() {
    emit('close');
    router.push({ name: 'rewards', query: { search: undefined }, force: true });
    rewardsStore.setSearchQuery('');
    searchQuery.value = '';
  }
</script>

<template>
  <BaseModal @close="$emit('close')" :dark="false">
    <div class="search-modal">
      <div class="search-field">
        <v-text-field
          :label="$t('nav.search')"
          variant="outlined"
          append-inner-icon="search"
          v-model="searchQuery"
          @keyup.enter="selectResult(searchQuery)"
        />
      </div>
      <div class="search-results">
        <v-list-item class="see-all-offers">
          <div @click="seeAllRewards()">
            {{ $t('nav.all-offers') }}
          </div>
        </v-list-item>
        <v-list
          v-for="(obj, n) in searchResults"
          :key="n"
        >
          <v-list-item>
            <div class="result pl-2" @click="selectResult(obj.brand)">
              {{ obj.brand }}
            </div>
          </v-list-item>
        </v-list>
      </div>
    </div>
  </BaseModal>
</template>

<style lang="scss" scoped>
  .search-modal {
    min-height: 250px;
    height: 100%;
    background-color: rgb(var(--v-theme-background));
    border-top-left-radius: 24px;
    border-top-right-radius: 24px;
    padding: 90px 22px 22px 22px;
  }

  :deep(.v-list-item--density-default.v-list-item--one-line) {
    min-height: 10px;
    color: rgb(var(--v-theme-neutral-400));
  }

  :deep(.v-list--one-line) {
    padding: 4px 0;
    background-color: transparent;
    transition: 0.5s ease background-color;
  }

  :deep(.v-list--one-line):hover {
    background-color: rgb(var(--v-theme-neutral-100));
  }

  .see-all-offers {
    font-weight: getFontWeight(bold);
    color: rgb(var(--v-theme-primary));
    transition: 0.5s ease background-color;

    &:hover {
      color: rgb(var(--v-theme-primary));
      cursor: pointer;
    }
  }
</style>